
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PhoneNumberInput } from "@/components/auth/PhoneNumberInput";
import { OTPVerification } from "@/components/auth/OTPVerification";
import { StatusMessage } from "@/components/auth/StatusMessage";
import { usePhoneVerification } from "@/hooks/usePhoneVerification";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { usePhoneInput } from "@/hooks/usePhoneInput";
import { useOTPInput } from "@/hooks/useOTPInput";
import { useVerificationState } from "@/hooks/useVerificationState";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export default function UserVerification() {
  const { verificationState, requestOTP, verifyOTP, resetState } = usePhoneVerification();
  const { phoneNumber, handlePhoneNumberChange, validatePhone } = usePhoneInput();
  const { otp, handleOTPChange, resetOTP, resendCooldown, startResendCooldown } = useOTPInput();
  const { showOTP, error, success, verified, loading, setShowOTP, setError, setSuccess, 
    setVerified, setLoading } = useVerificationState();
  const [sessionChecked, setSessionChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if there's an active session
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate('/account/subscription');
      }
      setSessionChecked(true);
    });
  }, [navigate]);

  // Add effect to handle redirect after verification
  useEffect(() => {
    let redirectTimer: number | undefined;
    
    if (verified) {
      // Set a timeout to give the user a chance to see the success message
      redirectTimer = window.setTimeout(() => {
        navigate('/account/subscription');
      }, 2500); // Redirect after 2.5 seconds
    }
    
    return () => {
      if (redirectTimer) {
        window.clearTimeout(redirectTimer);
      }
    };
  }, [verified, navigate]);

  const handleRequestOTP = async () => {
    if (!validatePhone()) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await requestOTP(phoneNumber);
      if (result) {
        setShowOTP(true);
        setSuccess("Verification code sent to your phone");
      }
    } catch (err: any) {
      setError(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Pass both OTP code and phone number to the verifyOTP function
      const result = await verifyOTP(otp, phoneNumber);
      if (result) {
        setVerified(true);
        setSuccess("Verification successful");
        
        // Login with the credentials returned from the verification
        if (result.credentials) {
          const { email, password } = result.credentials;
          await supabase.auth.signInWithPassword({
            email,
            password
          });
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to verify code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await requestOTP(phoneNumber);
      startResendCooldown();
      setSuccess("A new verification code has been sent");
    } catch (err: any) {
      setError(err.message || "Failed to resend verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    resetState();
    resetOTP();
    setShowOTP(false);
    setError(null);
    setSuccess(null);
    setVerified(false);
  };

  if (!sessionChecked) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="container flex items-center justify-center flex-grow py-8">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <p className="mt-2">Checking session...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="container flex items-center justify-center flex-grow py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              {verified ? "Verification Successful" : "Phone Verification"}
            </CardTitle>
            <CardDescription className="text-center">
              {verified
                ? "You're now verified and can manage your quote subscriptions."
                : showOTP
                ? "Enter the verification code sent to your phone"
                : "Enter your Australian mobile number to receive a verification code"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StatusMessage error={error} success={success} />

            {verified ? (
              <div className="text-center py-4">
                <p className="mb-4">Redirecting to your account...</p>
                <Loader2 className="animate-spin mx-auto h-6 w-6" />
              </div>
            ) : showOTP ? (
              <OTPVerification
                phoneNumber={phoneNumber}
                otp={otp}
                loading={loading}
                resendCooldown={resendCooldown}
                onOTPChange={handleOTPChange}
                onVerifyOTP={handleVerifyOTP}
                onResendOTP={handleResendOTP}
                onReset={handleReset}
                error={error}
              />
            ) : (
              <PhoneNumberInput
                phoneNumber={phoneNumber}
                loading={loading}
                onPhoneNumberChange={handlePhoneNumberChange}
                onRequestOTP={handleRequestOTP}
                error={error}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
